/**
 * Answers the one question a "back" affordance needs: would `router.back()` land
 * on an IN-APP page in THIS tab, or escape to the browser's start page? A link
 * opened in a new tab / a shared permalink / a cold reload has nothing in-app
 * behind it, so its "back" must climb to a fallback instead.
 *
 * We track our DEPTH in this tab's in-app navigation (the app's first page is 0),
 * using only primitives every browser supports — no Navigation API (absent on
 * older mobile OSes), no monkey-patching the History API (the App Router patches
 * `pushState`/`replaceState` itself and calls its own originals, so a wrapper
 * never sees its navigations), and no reliance on Next preserving custom
 * `history.state` keys. It is fed by {@link NavigationTracker}:
 *
 *  - PUSH (a new page) grows `window.history.length` → depth + 1.
 *  - POP  (browser back/forward) fires `popstate`, length unchanged → depth − 1.
 *  - REPLACE changes neither → depth unchanged.
 *
 * `window.history.length` alone is famously unreliable (a fresh tab already
 * reads ≥ 2: its blank entry + this page), but its DELTA between two consecutive
 * navigations is not — a push adds one, a replace adds none. At the true entry no
 * push has happened, replaces don't count, and a back into a cross-origin page
 * unloads the document (killing this listener) rather than firing `popstate` — so
 * depth stays 0 there and a back correctly falls back. Depth also decrements as
 * the user walks back, so returning to the entry re-arms the fallback.
 *
 * Module state resets on a full document load (new tab / reload) — the intended
 * scope. Remaining imprecision (the browser FORWARD button counts as a pop; a
 * push after a back truncates forward entries so its delta can be 0; browsers cap
 * `history.length` near 50) only ever makes a back use the fallback where it
 * could have popped, or vice versa — both land on a valid in-app page, never a
 * blank one.
 */

let depth = 0;
let lastHistoryLength = 0;
let initialized = false;
/**
 * The in-app pathnames behind us, oldest first — the same stack `depth` counts,
 * with the identities kept. Depth alone answers "is there something to go back
 * to"; some destinations are worse than none, and that needs the name.
 *
 * Same lifetime as `depth` (module state, reset by a full document load) and fed
 * by the same three signals: a push appends, a replace rewrites the top, a pop
 * drops it.
 */
const paths: string[] = [];

/**
 * Routes a back affordance must NEVER return to: they are CONSUMED states.
 *
 * `/signup` and `/login` redirect an already-authenticated creator away, so
 * popping into one bounces straight back out — and after onboarding they are
 * exactly what sits behind the page the creator is on (the funnel pushed
 * `/signup` → `/signup/finalize` → the pricing picker). Pressing a dismiss then
 * landed on the LOGIN page: the reported bug, seen first on the pricing picker's
 * ✕ once `isSponsoredCreator` revealed it.
 */
const kConsumedPathPrefixes = ['/login', '/signup'];

/** Is `pathname` a consumed state — see {@link kConsumedPathPrefixes}. */
export function isConsumedPath(pathname: string): boolean {
  return kConsumedPathPrefixes.some(
    base => pathname === base || pathname.startsWith(`${base}/`),
  );
}

/** The page `router.back()` would land on, when we know it. */
export function previousInAppPath(): string | undefined {
  return paths.length >= 2 ? paths[paths.length - 2] : undefined;
}

/**
 * Would `router.back()` land somewhere WORTH returning to — in-app, and not a
 * consumed state? The question a dismiss should ask before popping, rather than
 * `hasInAppHistory()` alone.
 *
 * An unknown previous page (tracking started mid-session) is treated as safe, so
 * this can only ever be more conservative than the depth check it wraps.
 */
export function canPopToUsefulPage(): boolean {
  if (!hasInAppHistory()) return false;
  const previous = previousInAppPath();
  return previous == null || !isConsumedPath(previous);
}

/**
 * Reconcile depth after a committed navigation, given the current
 * `window.history.length`. A grown length is a push (deeper); an unchanged
 * length is a replace (pops are handled by {@link recordPop} instead). The first
 * call just captures the baseline.
 */
export function syncNavigation(historyLength: number, pathname?: string): void {
  if (!initialized) {
    initialized = true;
    lastHistoryLength = historyLength;
    if (pathname != null) paths.push(pathname);
    return;
  }
  if (historyLength > lastHistoryLength) {
    depth += 1;
    if (pathname != null) paths.push(pathname);
  } else if (pathname != null) {
    // A replace: same entry, different page — rewrite the top rather than
    // stacking, or `previousInAppPath` would name a page nothing can return to.
    if (paths.length > 0) paths[paths.length - 1] = pathname;
    else paths.push(pathname);
  }
  lastHistoryLength = historyLength;
}

/** Record a browser back/forward (`popstate`) — one step shallower. */
export function recordPop(): void {
  depth = Math.max(0, depth - 1);
  paths.pop();
}

/** Whether `router.back()` would stay in-app in this tab. */
export function hasInAppHistory(): boolean {
  return depth > 0;
}
